import { Visibility, VisibilityOff } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { useResultsStore } from '@editorStores'

/**
 * Eye column toggling scene visibility per load, with a header eye for the whole tab — backed by
 * the same `hiddenLoads` state the element load tables and the 3D scene use.
 */
const useLoadVisibilityColumn = (): ((rows: { loadGuids: string[] }[]) => GridColDef) => {
  const hiddenLoads = useResultsStore(state => state.hiddenLoads)
  const setHiddenLoads = useResultsStore(state => state.setHiddenLoads)

  const areLoadsHidden = (guids: string[]): boolean =>
    guids.length > 0 && guids.every(guid => hiddenLoads.includes(guid))

  const setLoadsHiddenState = (guids: string[], hidden: boolean) => {
    const next = new Set(hiddenLoads)
    guids.forEach(guid => (hidden ? next.add(guid) : next.delete(guid)))
    setHiddenLoads(Array.from(next))
  }

  return (rows: { loadGuids: string[] }[]): GridColDef => {
    const allGuids = rows.flatMap(row => row.loadGuids)
    const allHidden = areLoadsHidden(allGuids)
    return {
      field: 'visibility',
      headerName: 'Sichtbarkeit',
      width: 34,
      minWidth: 34,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      renderHeaderFilter: () => null,
      renderHeader: () => (
        <IconButton size="small" onClick={() => setLoadsHiddenState(allGuids, !allHidden)}>
          {allHidden ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
        </IconButton>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const guids = (params.row.loadGuids ?? []) as string[]
        const hidden = areLoadsHidden(guids)
        return (
          <IconButton
            size="small"
            onClick={event => {
              event.stopPropagation()
              setLoadsHiddenState(guids, !hidden)
            }}
          >
            {hidden ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
          </IconButton>
        )
      },
    }
  }
}

export default useLoadVisibilityColumn
